import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Induction, Site, Worker } from '../lib/supabase';
import { InductionForm } from '../components/forms/InductionForm';
import { generateInductionPDF } from '../utils/pdfGenerator';
import { generateSiteInductionsPDF } from '../utils/siteInductionsPDF';
import { 
  Plus, 
  ClipboardCheck, 
  AlertTriangle, 
  CheckCircle, 
  Users, 
  Download,
  Building2,
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  X
} from 'lucide-react';

interface InductionWithDetails extends Induction {
  site?: Site;
  worker?: Worker;
}

interface SiteWithInductions {
  site: Site;
  inductions: InductionWithDetails[];
  completedCount: number;
  pendingCount: number;
  totalCount: number;
}

export const InductionsPage: React.FC = () => {
  const [siteInductions, setSiteInductions] = useState<SiteWithInductions[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [showHazardsModal, setShowHazardsModal] = useState(false);
  const [selectedSiteForHazards, setSelectedSiteForHazards] = useState<SiteWithInductions | null>(null);
  const [siteHazards, setSiteHazards] = useState('');
  const [loadingHazards, setLoadingHazards] = useState(false);
  const [savingHazards, setSavingHazards] = useState(false);

  const handleDownloadInduction = (induction: InductionWithDetails) => {
    // Ensure we have complete site and worker data before generating PDF
    if (!induction.site || !induction.worker) {
      console.error('Missing site or worker data for PDF generation');
      alert('Unable to generate PDF: Missing site or worker information');
      return;
    }
    generateInductionPDF(induction);
  };

  const handleDownloadSiteInductions = (siteInduction: SiteWithInductions) => {
    generateSiteInductionsPDF(siteInduction);
  };

  const handleViewHazards = async (siteInduction: SiteWithInductions) => {
    setSelectedSiteForHazards(siteInduction);
    setShowHazardsModal(true);
    setLoadingHazards(true);
    
    try {
      const { data, error } = await supabase
        .from('site_specific_content')
        .select('content')
        .eq('site_id', siteInduction.site.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching site hazards:', error);
        setSiteHazards('');
      } else {
        setSiteHazards(data?.content || '');
      }
    } catch (error) {
      console.error('Error fetching site hazards:', error);
      setSiteHazards('');
    } finally {
      setLoadingHazards(false);
    }
  };

  const handleSaveHazards = async () => {
    if (!selectedSiteForHazards) return;
    
    setSavingHazards(true);
    try {
      const { error } = await supabase
        .from('site_specific_content')
        .upsert({
          site_id: selectedSiteForHazards.site.id,
          content: siteHazards
        }, {
          onConflict: 'site_id'
        });

      if (error) {
        console.error('Error saving site hazards:', error);
        alert('Failed to save hazards. Please try again.');
      } else {
        setShowHazardsModal(false);
        setSelectedSiteForHazards(null);
        setSiteHazards('');
        alert('Site hazards saved successfully!');
      }
    } catch (error) {
      console.error('Error saving site hazards:', error);
      alert('Failed to save hazards. Please try again.');
    } finally {
      setSavingHazards(false);
    }
  };

  useEffect(() => {
    fetchInductions();
  }, []);

  const fetchInductions = async () => {
    try {
      // Fetch all sites
      const { data: sitesData, error: sitesError } = await supabase
        .from('sites')
        .select('*')
        .order('name');

      if (sitesError) throw sitesError;

      // Fetch all inductions with worker details
      const { data: inductionsData, error: inductionsError } = await supabase
        .from('inductions')
        .select(`
          *,
          workers!inner(id, name, role, email, phone, start_date, certifications, status, whitecard_number),
          sites!inner(id, name, type, address, start_date, end_date, description, requirements, photo_url, status, asbestos_type)
        `)
        .order('created_at', { ascending: false });

      if (inductionsError) throw inductionsError;

      // Group inductions by site
      const siteInductionsMap = new Map<string, InductionWithDetails[]>();
      
      // Initialize all sites with empty arrays
      sitesData?.forEach(site => {
        siteInductionsMap.set(site.id, []);
      });

      // Group inductions by site
      inductionsData?.forEach(induction => {
        const siteId = induction.site_id;
        if (!siteInductionsMap.has(siteId)) {
          siteInductionsMap.set(siteId, []);
        }
        
        const inductionWithDetails: InductionWithDetails = {
          ...induction,
          worker: induction.workers,
          site: induction.sites
        };
        
        siteInductionsMap.get(siteId)?.push(inductionWithDetails);
      });

      // Create the final structure
      const siteInductionsArray: SiteWithInductions[] = [];
      
      sitesData?.forEach(site => {
        const inductions = siteInductionsMap.get(site.id) || [];
        const completedCount = inductions.filter(i => i.status === 'completed').length;
        const pendingCount = inductions.filter(i => i.status === 'pending').length;
        
        siteInductionsArray.push({
          site,
          inductions,
          completedCount,
          pendingCount,
          totalCount: inductions.length
        });
      });

      // Sort sites by total inductions (most active first), then by name
      siteInductionsArray.sort((a, b) => {
        if (a.totalCount !== b.totalCount) {
          return b.totalCount - a.totalCount;
        }
        return a.site.name.localeCompare(b.site.name);
      });

      setSiteInductions(siteInductionsArray);
      
      // Start with all sites collapsed
      setExpandedSites(new Set());
      
    } catch (error) {
      console.error('Error fetching inductions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (inductionData: Partial<Induction>) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('inductions')
        .insert([inductionData]);
      
      if (error) throw error;
      
      setShowModal(false);
      fetchInductions();
    } catch (error) {
      console.error('Error saving induction:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (inductionId: string) => {
    if (confirm('Are you sure you want to delete this induction? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('inductions')
          .delete()
          .eq('id', inductionId);
        
        if (error) throw error;
        fetchInductions();
      } catch (error) {
        console.error('Error deleting induction:', error);
      }
    }
  };

  const toggleSiteExpansion = (siteId: string) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
    }
    setExpandedSites(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <ClipboardCheck className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const totalInductions = siteInductions.reduce((sum, si) => sum + si.totalCount, 0);
  const totalCompleted = siteInductions.reduce((sum, si) => sum + si.completedCount, 0);
  const totalPending = siteInductions.reduce((sum, si) => sum + si.pendingCount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Induction Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            {totalInductions} total inductions • {totalCompleted} completed • {totalPending} pending
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>New Induction</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Active Sites</p>
              <p className="text-xl font-bold text-gray-900">
                {siteInductions.filter(si => si.site.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Inductions</p>
              <p className="text-xl font-bold text-gray-900">{totalInductions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-xl font-bold text-gray-900">{totalCompleted}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-xl font-bold text-gray-900">{totalPending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Site Hierarchy */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Inductions by Site</h3>
          <p className="text-sm text-gray-500 mt-1">Click on sites to expand and view individual induction records</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {siteInductions.map((siteInduction) => (
            <div key={siteInduction.site.id}>
              {/* Site Header */}
              <div 
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => toggleSiteExpansion(siteInduction.site.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {expandedSites.has(siteInduction.site.id) ? (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                      <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-orange-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{siteInduction.site.name}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="capitalize">{siteInduction.site.type.replace('_', ' ')}</span>
                        <span>•</span>
                        <span>{siteInduction.site.address}</span>
                        <span>•</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          siteInduction.site.status === 'active' ? 'bg-green-100 text-green-800' :
                          siteInduction.site.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {siteInduction.site.status.charAt(0).toUpperCase() + siteInduction.site.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-800">{siteInduction.completedCount}</span>
                          <span className="text-gray-500">completed</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4 text-yellow-600" />
                          <span className="font-medium text-yellow-800">{siteInduction.pendingCount}</span>
                          <span className="text-gray-500">pending</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClipboardCheck className="h-4 w-4 text-gray-600" />
                          <span className="font-medium text-gray-900">{siteInduction.totalCount}</span>
                          <span className="text-gray-500">total</span>
                        </div>
                      </div>
                    </div>
                    
                    <Link
                      to={`/sites/${siteInduction.site.id}`}
                      className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Site
                    </Link>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewHazards(siteInduction);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm"
                      title="Manage site-specific hazards"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <span>Hazards</span>
                    </button>
                   
                    {siteInduction.completedCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadSiteInductions(siteInduction);
                        }}
                        className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                        title="Download all completed inductions for this site"
                      >
                        <Download className="h-4 w-4" />
                        <span>Download Report</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Inductions */}
              {expandedSites.has(siteInduction.site.id) && (
                <div className="bg-gray-50">
                  {siteInduction.inductions.length > 0 ? (
                    <div className="px-6 py-4">
                      <div className="space-y-3">
                        {siteInduction.inductions.map((induction) => (
                          <div key={induction.id} className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  {getStatusIcon(induction.status)}
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(induction.status)}`}>
                                    {induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
                                  </span>
                                </div>
                                
                                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Users className="h-4 w-4 text-blue-600" />
                                </div>
                                
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{induction.worker?.name}</p>
                                  <p className="text-xs text-gray-500 capitalize">{induction.worker?.role?.replace('_', ' ')}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-4">
                                <div className="text-right text-xs text-gray-500">
                                  <p>Created: {new Date(induction.created_at).toLocaleDateString()}</p>
                                  {induction.completed_at && (
                                    <p>Completed: {new Date(induction.completed_at).toLocaleDateString()}</p>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <Link
                                    to={`/inductions/${induction.id}`}
                                    className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                                  >
                                    <Eye className="h-4 w-4" />
                                    <span className="text-xs">View</span>
                                  </Link>
                                  
                                  {induction.status === 'completed' && (
                                    <button
                                      onClick={() => handleDownloadInduction(induction)}
                                      className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                                      title="Download Certificate"
                                    >
                                      <Download className="h-4 w-4" />
                                      <span className="text-xs">Download</span>
                                    </button>
                                  )}
                                  
                                  <button
                                    onClick={() => handleDelete(induction.id)}
                                    className="text-red-600 hover:text-red-900 text-xs"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <ClipboardCheck className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No inductions recorded for this site yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {siteInductions.length === 0 && (
        <div className="text-center py-12">
          <ClipboardCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites found</h3>
          <p className="text-gray-500 mb-4">Create sites first, then process inductions for workers.</p>
          <Link
            to="/sites"
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Manage Sites
          </Link>
        </div>
      )}

      {showModal && (
        <InductionForm
          onSubmit={handleSubmit}
          onClose={() => setShowModal(false)}
          loading={submitting}
        />
      )}

      {/* Site Hazards Modal */}
      {showHazardsModal && selectedSiteForHazards && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Site-Specific Hazards</h3>
                  <p className="text-sm text-gray-500">{selectedSiteForHazards.site.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowHazardsModal(false);
                  setSelectedSiteForHazards(null);
                  setSiteHazards('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Site Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-2">Site Information</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Address:</strong> {selectedSiteForHazards.site.address}</p>
                  <p><strong>Type:</strong> {selectedSiteForHazards.site.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p><strong>Status:</strong> {selectedSiteForHazards.site.status.charAt(0).toUpperCase() + selectedSiteForHazards.site.status.slice(1)}</p>
                </div>
              </div>

              {/* Hazards Text Area */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site-Specific Hazards and Safety Information
                </label>
                {loadingHazards ? (
                  <div className="flex items-center justify-center p-8 border border-gray-300 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading existing hazards...</span>
                  </div>
                ) : (
                  <textarea
                    value={siteHazards}
                    onChange={(e) => setSiteHazards(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Enter site-specific hazards and safety information...

Examples:
• Overhead power lines on the eastern boundary
• Asbestos sheeting identified in roof areas
• Unstable retaining wall on north side - keep 3m clearance
• Chemical contamination in soil - testing required before excavation
• Restricted access hours: 7am-5pm weekdays only
• Noise restrictions: No work before 8am or after 4pm
• Emergency assembly point: Front gate on Main Street
• Site contact: John Smith - 0400 123 456"
                  />
                )}
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    Document all site-specific hazards, restrictions, and safety requirements
                  </p>
                  <p className="text-xs text-gray-500">
                    {siteHazards.length} characters
                  </p>
                </div>
              </div>

              {/* Guidelines */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Guidelines - What to Include:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Physical Hazards:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Overhead power lines</li>
                      <li>Underground utilities</li>
                      <li>Unstable structures</li>
                      <li>Excavation areas</li>
                      <li>Height restrictions</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Chemical/Environmental:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Asbestos locations</li>
                      <li>Contaminated soil</li>
                      <li>Chemical storage areas</li>
                      <li>Air quality concerns</li>
                      <li>Water contamination</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Access & Restrictions:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Working hours</li>
                      <li>Noise limitations</li>
                      <li>Traffic management</li>
                      <li>Restricted areas</li>
                      <li>Permit requirements</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 mb-1">Emergency Information:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Assembly points</li>
                      <li>Emergency contacts</li>
                      <li>Evacuation routes</li>
                      <li>First aid locations</li>
                      <li>Site supervisor details</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowHazardsModal(false);
                    setSelectedSiteForHazards(null);
                    setSiteHazards('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveHazards}
                  disabled={savingHazards}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                >
                  {savingHazards ? 'Saving...' : 'Save Hazards'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};